import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SEOPages } from '../components/SEO';
import { 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  ArrowRight,
  Calculator,
  FileText,
  Award,
  DollarSign,
  Clock
} from 'lucide-react';
import LeadMagnetBox from '../components/LeadMagnetBox';
import MiniSimulatorRachat2P from '../components/MiniSimulatorRachat2P';

const GuideRachat2emePilier = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F12] via-[#0C1D3D] to-[#0B0F12]">
      <SEO {...SEOPages.guideRachat2emePilier} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent leading-tight">
              Guide Complet Rachat 2ème Pilier
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              Optimisez votre prévoyance et économisez des milliers de francs d'impôts
            </p>
            <p className="text-sm text-gray-400">
              Par ATOM Group SA • Mis à jour le {new Date().toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })} • Lecture : 15 minutes
            </p>
          </div>

          {/* Lead Magnet Top */}
          <LeadMagnetBox position="top" />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">

          {/* Applicabilité nationale */}
          <div className="p-5 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg mb-8">
            <p className="text-gray-200 text-sm">
              <strong>🇨🇭 Guide applicable dans toute la Suisse</strong> • Les règles LPP sont fédérales. Les avantages fiscaux du rachat varient selon votre canton, mais les principes sont identiques partout en Suisse romande (Vaud, Genève, Neuchâtel, Fribourg, Valais). <strong>Mis à jour : {new Date().toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' })}</strong>
            </p>
          </div>

          {/* Introduction */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Qu'est-ce que le rachat 2ème pilier ?</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Le rachat dans le 2ème pilier (LPP) permet de combler les lacunes de votre prévoyance professionnelle tout en bénéficiant d'un avantage fiscal considérable. C'est l'une des stratégies d'optimisation fiscale les plus puissantes en Suisse.
            </p>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Contrairement au 3ème pilier qui a un plafond fixe, le rachat 2ème pilier peut représenter des montants bien plus élevés (parfois plus de CHF 100'000), offrant ainsi une déduction fiscale exceptionnelle.
            </p>
            
            {/* ATOM Independence */}
            <div className="mt-6 p-4 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant,</strong> non lié à une banque ou assurance. Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* Lead Magnet après intro */}

          {/* CTA après introduction */}
          

          {/* Pourquoi racheter */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="w-8 h-8 text-green-400" />
              <h2 className="text-3xl font-bold text-white">Pourquoi racheter son 2ème pilier ?</h2>
            </div>
            
            <div className="space-y-4">
              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  1. Déduction fiscale immédiate et majeure
                </h4>
                <p className="text-gray-300 text-sm">
                  Le montant racheté est entièrement déductible de votre revenu imposable. Pour un rachat de CHF 50'000 avec un taux d'imposition de 30%, vous économisez CHF 15'000 d'impôts !
                </p>
              </div>

              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  2. Amélioration de votre rente de retraite
                </h4>
                <p className="text-gray-300 text-sm">
                  Chaque franc racheté augmente votre capital LPP et donc votre future rente de retraite. C'est un double avantage : économie fiscale aujourd'hui + sécurité demain.
                </p>
              </div>

              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  3. Protection et rendement garanti
                </h4>
                <p className="text-gray-300 text-sm">
                  Votre capital LPP est protégé et bénéficie d'un taux d'intérêt minimal garanti (actuellement 1.25% sur la part obligatoire). C'est sûr et sécurisé.
                </p>
              </div>

              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  4. Flexibilité : retrait possible pour l'immobilier
                </h4>
                <p className="text-gray-300 text-sm">
                  Le capital racheté peut être retiré pour financer l'achat de votre résidence principale (sous certaines conditions), offrant ainsi une stratégie patrimoniale complète.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          

          {/* Comment calculer */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Calculator className="w-8 h-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-white">Comment calculer votre potentiel de rachat ?</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Votre caisse de pension calcule votre lacune de prévoyance en comparant votre avoir actuel avec l'avoir théorique que vous devriez avoir. Les lacunes peuvent provenir de :
            </p>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-2 text-gray-300">
                <ArrowRight className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span><strong>Années d'études</strong> : Période sans cotisations LPP</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <ArrowRight className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span><strong>Augmentations de salaire</strong> : Passage à un salaire supérieur</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <ArrowRight className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span><strong>Années à l'étranger</strong> : Périodes sans affiliation suisse</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <ArrowRight className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span><strong>Temps partiel</strong> : Cotisations réduites</span>
              </li>
              <li className="flex items-start gap-2 text-gray-300">
                <ArrowRight className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span><strong>Divorce</strong> : Partage de l'avoir LPP</span>
              </li>
            </ul>

            <div className="p-6 bg-blue-500/10 border-l-4 border-blue-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>Important :</strong> Demandez votre certificat LPP à votre employeur pour connaître votre potentiel de rachat exact. Ce montant figure généralement sur la page 2 du certificat.
              </p>
            </div>
          </div>

          {/* Simulateur */}
          <MiniSimulatorRachat2P />

          {/* Avantages fiscaux concrets */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-8 h-8 text-green-400" />
              <h2 className="text-3xl font-bold text-white">Avantages fiscaux concrets</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              Voici des exemples concrets d'économies fiscales avec le rachat 2ème pilier en Suisse romande :
            </p>

            <div className="space-y-4">
              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2">💼 Cadre - Revenu CHF 120'000</h4>
                <p className="text-gray-300 mb-2">Rachat : CHF 30'000</p>
                <p className="text-2xl font-bold text-green-400">Économie fiscale : ~CHF 9'000</p>
                <p className="text-sm text-gray-400 mt-1">Taux marginal : 30% (cantonal + fédéral)</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2">👨‍👩‍👧 Couple - Revenu CHF 180'000</h4>
                <p className="text-gray-300 mb-2">Rachat total (2 personnes) : CHF 60'000</p>
                <p className="text-2xl font-bold text-green-400">Économie fiscale : ~CHF 19'800</p>
                <p className="text-sm text-gray-400 mt-1">Taux marginal combiné : 33%</p>
              </div>

              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
                <h4 className="font-bold text-white mb-2">🚀 Dirigeant - Revenu CHF 250'000</h4>
                <p className="text-gray-300 mb-2">Rachat exceptionnel : CHF 100'000</p>
                <p className="text-2xl font-bold text-green-400">Économie fiscale : ~CHF 38'000</p>
                <p className="text-sm text-gray-400 mt-1">Taux marginal : 38% (revenu élevé)</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant.</strong> Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* CTA */}
          

          {/* CTA WhatsApp rapide */}
          <div className="glass p-6 rounded-xl border border-green-500/30 mb-8 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="text-3xl">📲</div>
                <div>
                  <p className="text-white font-semibold text-lg">Une question rapide ?</p>
                  <p className="text-gray-300 text-sm">Réponse en 5 minutes</p>
                </div>
              </div>
              <a
                href="https://wa.me/41793754293"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105 flex items-center gap-2 whitespace-nowrap"
              >
                Écrire sur WhatsApp
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* CTA Simulation Rachat */}
          <div className="glass p-8 rounded-2xl border border-purple-500/30 mb-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
            <div className="text-center">
              <div className="inline-block bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-xl mb-4">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                💡 Vous souhaitez connaître votre potentiel exact de rachat ?
              </h3>
              <p className="text-gray-300 mb-6">
                Obtenez votre montant rachetable + votre économie fiscale estimée en 45 secondes
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 text-lg"
              >
                Lancer la simulation ATOM (gratuit – 45 secondes)
                <ArrowRight className="w-6 h-6" />
              </Link>
            </div>
          </div>

          {/* Lead Magnet Middle */}

          {/* Stratégies de rachat */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-8 h-8 text-purple-400" />
              <h2 className="text-3xl font-bold text-white">Stratégies de rachat optimales</h2>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">1️⃣ Rachat unique vs échelonné</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-500/30">
                <h4 className="font-bold text-white mb-3">💥 Rachat unique</h4>
                <p className="text-gray-300 mb-3 text-sm"><strong>Avantages :</strong></p>
                <ul className="text-gray-300 text-sm space-y-1 mb-3">
                  <li>• Simplicité administrative</li>
                  <li>• Impact fiscal immédiat maximal</li>
                </ul>
                <p className="text-gray-300 mb-2 text-sm"><strong>Idéal pour :</strong></p>
                <p className="text-gray-300 text-sm">Montants &lt; CHF 25'000 ou revenus exceptionnels (bonus, vente)</p>
              </div>

              <div className="p-6 bg-purple-500/10 rounded-xl border border-purple-500/30">
                <h4 className="font-bold text-white mb-3">📈 Rachat échelonné</h4>
                <p className="text-gray-300 mb-3 text-sm"><strong>Avantages :</strong></p>
                <ul className="text-gray-300 text-sm space-y-1 mb-3">
                  <li>• Évite la progression fiscale excessive</li>
                  <li>• Optimisation sur plusieurs années</li>
                  <li>• Flexibilité financière</li>
                </ul>
                <p className="text-gray-300 mb-2 text-sm"><strong>Idéal pour :</strong></p>
                <p className="text-gray-300 text-sm">Montants &gt; CHF 30'000, planification 3-5 ans avant retraite</p>
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-4 mt-8">2️⃣ Timing optimal</h3>
            
            <div className="space-y-4">
              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Meilleur moment : Fin d'année (novembre-décembre)</h4>
                <p className="text-gray-300 text-sm">
                  Permet de connaître précisément votre revenu annuel et d'optimiser la déduction fiscale. 
                  Attention : le versement doit être effectué avant le 31 décembre pour l'année en cours.
                </p>
              </div>

              <div className="p-6 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">⚠️ Restriction importante : Règle des 3 ans</h4>
                <p className="text-gray-300 text-sm">
                  Après un rachat, vous ne pouvez pas retirer votre capital LPP (sous forme de capital à la retraite ou pour l'immobilier) 
                  pendant 3 ans. Planifiez en conséquence !
                </p>
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-4 mt-8">3️⃣ Stratégie avant la retraite</h3>
            
            <p className="text-gray-300 mb-4 leading-relaxed">
              Les 5-10 ans avant la retraite sont idéaux pour des rachats échelonnés. Stratégie recommandée :
            </p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-green-400 font-bold">1.</span>
                <span>Calculer le potentiel total de rachat</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 font-bold">2.</span>
                <span>Diviser en 3-5 tranches annuelles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 font-bold">3.</span>
                <span>Privilégier les années à revenus élevés (bonus, vente d'actifs)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 font-bold">4.</span>
                <span>Respecter la règle des 3 ans avant tout retrait</span>
              </li>
            </ul>
          </div>

          {/* Erreurs à éviter */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <h2 className="text-3xl font-bold text-white">5 Erreurs fréquentes à éviter</h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  title: "Racheter sans vérifier le certificat LPP",
                  desc: "Demandez toujours votre certificat LPP actuel pour connaître le montant exact rachetable et éviter les erreurs"
                },
                {
                  title: "Ignorer la règle des 3 ans",
                  desc: "Si vous planifiez un retrait (retraite anticipée, achat immobilier), ne rachetez pas dans les 3 ans précédents"
                },
                {
                  title: "Racheter trop tôt dans l'année",
                  desc: "Privilégiez la fin d'année pour mieux évaluer votre revenu imposable et optimiser la déduction"
                },
                {
                  title: "Oublier de combiner avec le 3ème pilier",
                  desc: "Le rachat 2ème pilier et le 3a sont complémentaires. Utilisez les deux pour maximiser vos déductions fiscales"
                },
                {
                  title: "Échelonner les gros rachats",
                  desc: "Pour des rachats &gt; CHF 50'000, privilégiez un échelonnement sur 3-5 ans pour optimiser la fiscalité"
                }
              ].map((error, index) => (
                <div key={index} className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                  <h4 className="font-bold text-white mb-2">❌ {error.title}</h4>
                  <p className="text-gray-300 text-sm">{error.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Questions fréquentes</h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "Puis-je racheter si j'ai déjà un 3ème pilier ?",
                  a: "Oui, absolument ! Le rachat 2ème pilier et le 3ème pilier sont totalement indépendants et cumulables. Vous pouvez même les utiliser la même année pour maximiser vos déductions fiscales."
                },
                {
                  q: "Quel est le montant maximum que je peux racheter ?",
                  a: "Le montant maximum figure sur votre certificat LPP (page 2, section 'rachat possible'). Il dépend de votre âge, salaire, et historique de cotisations. Il peut aller de quelques milliers à plus de CHF 200'000."
                },
                {
                  q: "Le rachat est-il intéressant si je veux prendre ma retraite en capital ?",
                  a: "Oui, très ! Même si vous prenez votre LPP en capital, le rachat reste avantageux : déduction fiscale immédiate importante, puis taxation réduite du capital à la retraite (taux séparé avantageux)."
                },
                {
                  q: "Puis-je racheter si je prévois d'acheter une maison ?",
                  a: "Oui, mais attention à la règle des 3 ans ! Si vous prévoyez un retrait pour l'immobilier, effectuez vos rachats au moins 3 ans avant pour éviter les pénalités fiscales."
                },
                {
                  q: "Que se passe-t-il si je change d'employeur après un rachat ?",
                  a: "Votre avoir LPP, incluant le montant racheté, est transféré automatiquement à votre nouvelle caisse de pension. Vous conservez tous les avantages du rachat."
                }
              ].map((faq, index) => (
                <div key={index} className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-green-500/30 transition-all">
                  <h4 className="font-bold text-white mb-2 flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-300 text-sm ml-7">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Lead Magnet Bottom */}

          {/* Conclusion */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Le rachat 2ème pilier est l'une des stratégies d'optimisation fiscale les plus puissantes en Suisse. 
              Contrairement au 3ème pilier limité à CHF 7'258, le rachat LPP peut représenter des montants bien plus importants, 
              offrant des économies fiscales exceptionnelles.
            </p>
            <p className="text-gray-300 mb-6 leading-relaxed">
              <strong className="text-white">Ne laissez pas passer cette opportunité :</strong> combiné avec le 3ème pilier, 
              le rachat 2ème pilier vous permet d'optimiser votre prévoyance tout en réduisant significativement votre charge fiscale.
            </p>

            <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
              <p className="text-white font-semibold mb-4">
                🎯 ATOM Group vous accompagne pour :
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Analyser votre certificat LPP et calculer votre potentiel exact de rachat
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Définir la stratégie optimale (rachat unique vs échelonné)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Planifier le timing idéal en fonction de votre situation
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  Combiner rachat 2ème pilier et 3ème pilier pour maximiser vos avantages fiscaux
                </li>
              </ul>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105 text-lg"
              >
                Réserver mon analyse gratuite maintenant
                <ArrowRight className="w-6 h-6" />
              </Link>
              <p className="text-gray-400 text-sm mt-3">
                🕐 Rendez-vous de 30 minutes • 100% gratuit • Sans engagement
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Sticky Banner */}

      {/* Exit Intent Popup */}
    </div>
  );
};

export default GuideRachat2emePilier;